import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Database } from '@cafe-pos/supabase';
import { supabase } from '../supabase.js';
import { parseCsv, readFileAsText } from '../lib/csv.js';
import { UNITS } from '../constants.js';
import { AlertTriangle, Check, ChefHat, ChevronRight, CircleOff, Download, History, Trash2 } from 'lucide-react';
import { Button, Callout, EmptyState, Modal, PageHeader } from '../ui/index.js';

type IngredientRow = Database['public']['Tables']['ingredients']['Row'];
type InvTxRow = Database['public']['Tables']['inventory_transactions']['Row'];
type RecipeLink = { id: string; name: string };

async function loadRecipesUsingIngredient(ingredientId: string): Promise<RecipeLink[]> {
  if (!supabase) return [];
  const ids = new Set<string>();

  const { data: items } = await supabase
    .from('recipe_items')
    .select('recipe_id')
    .eq('ingredient_id', ingredientId);
  for (const row of items ?? []) ids.add(row.recipe_id);

  const { data: subs } = await supabase
    .from('recipe_item_substitutes')
    .select('recipe_item_id')
    .eq('ingredient_id', ingredientId);
  const subItemIds = [...new Set((subs ?? []).map((r) => r.recipe_item_id))];
  if (subItemIds.length) {
    const { data: parents } = await supabase
      .from('recipe_items')
      .select('recipe_id')
      .in('id', subItemIds);
    for (const row of parents ?? []) ids.add(row.recipe_id);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: choices } = await (supabase as any)
    .from('recipe_choice_options')
    .select('group_id')
    .eq('ingredient_id', ingredientId);
  const groupIds = [...new Set((choices ?? []).map((c: { group_id: string }) => c.group_id).filter(Boolean))];
  if (groupIds.length) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: groups } = await (supabase as any)
      .from('recipe_choice_groups')
      .select('recipe_id')
      .in('id', groupIds);
    for (const g of groups ?? []) ids.add(g.recipe_id);
  }

  if (ids.size === 0) return [];
  const { data: recs } = await supabase.from('recipes').select('id, name').in('id', [...ids]);
  return [...(recs ?? [])].sort((a, b) => a.name.localeCompare(b.name));
}

interface NewIngredient {
  name: string;
  unit: string;
  stock: string;
  lowStockThreshold: string;
  costPrice: string;
}
const BLANK: NewIngredient = {
  name: '',
  unit: 'kg',
  stock: '',
  lowStockThreshold: '',
  costPrice: '',
};

const RECIPES_MODAL_KEY = 'orio.ingredients.recipesFor';

function readRecipesModalId(): string | null {
  const fromHash = new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('recipes');
  if (fromHash?.trim()) return fromHash.trim();
  try {
    return sessionStorage.getItem(RECIPES_MODAL_KEY);
  } catch {
    return null;
  }
}

function persistRecipesModalId(ingredientId: string | null) {
  try {
    if (ingredientId) sessionStorage.setItem(RECIPES_MODAL_KEY, ingredientId);
    else sessionStorage.removeItem(RECIPES_MODAL_KEY);
  } catch {
    /* ignore quota / private mode */
  }
  if (!window.location.hash.startsWith('#/ingredients')) return;
  const params = new URLSearchParams(window.location.hash.split('?')[1] ?? '');
  if (ingredientId) params.set('recipes', ingredientId);
  else params.delete('recipes');
  const qs = params.toString();
  const next = qs ? `#/ingredients?${qs}` : '#/ingredients';
  if (window.location.hash !== next) {
    window.history.replaceState(null, '', next);
  }
}

export function IngredientsManager({
  clientId,
  role,
  onNavigate,
  onNavigateToRecipe,
}: {
  clientId: string | null;
  role?: string;
  onNavigate?: (view: string) => void;
  onNavigateToRecipe?: (recipeId: string) => void;
}) {
  const { t } = useTranslation();
  const [ingredients, setIngredients] = useState<IngredientRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState<NewIngredient>(BLANK);
  const [saving, setSaving] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState<string | null>(null);
  const csvRef = useRef<HTMLInputElement>(null);

  // inline restock
  const [restockId, setRestockId] = useState<string | null>(null);
  const [restockQty, setRestockQty] = useState('');

  // inline adjustment (waste / count correction)
  const [adjustId, setAdjustId] = useState<string | null>(null);
  const [adjustDelta, setAdjustDelta] = useState('');
  const [adjustNote, setAdjustNote] = useState('');

  // inline low-stock threshold edit
  const [lowStockEditId, setLowStockEditId] = useState<string | null>(null);
  const [lowStockEditVal, setLowStockEditVal] = useState('');

  // inline reorder-quantity edit
  const [reorderEditId, setReorderEditId] = useState<string | null>(null);
  const [reorderEditVal, setReorderEditVal] = useState('');

  // inline name edit
  const [nameEditId, setNameEditId] = useState<string | null>(null);
  const [nameEditVal, setNameEditVal] = useState('');

  // inline cost-price edit
  const [costEditId, setCostEditId] = useState<string | null>(null);
  const [costEditVal, setCostEditVal] = useState('');

  // table search
  const [search, setSearch] = useState('');

  type SortCol = 'name' | 'unit' | 'stock' | 'low_stock' | 'reorder' | 'cost_price' | 'value';
  type SortDir = 'asc' | 'desc';
  const [sortCol, setSortCol] = useState<SortCol>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  function toggleSort(col: SortCol) {
    if (sortCol === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortCol(col);
      setSortDir('asc');
    }
  }

  function sortIndicator(col: SortCol) {
    if (sortCol !== col) return ' ↕';
    return sortDir === 'asc' ? ' ↑' : ' ↓';
  }

  const displayedIngredients = useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = ingredients.filter((ing) => ing.name.toLowerCase().includes(q));
    return [...filtered].sort((a, b) => {
      let cmp = 0;
      switch (sortCol) {
        case 'name':
          cmp = a.name.localeCompare(b.name);
          break;
        case 'unit':
          cmp = a.unit.localeCompare(b.unit);
          break;
        case 'stock':
          cmp = Number(a.stock) - Number(b.stock);
          break;
        case 'low_stock':
          cmp = Number(a.low_stock_threshold) - Number(b.low_stock_threshold);
          break;
        case 'reorder':
          cmp = Number(a.reorder_quantity ?? 0) - Number(b.reorder_quantity ?? 0);
          break;
        case 'cost_price':
          cmp = Number(a.cost_price) - Number(b.cost_price);
          break;
        case 'value':
          cmp = Number(a.stock) * Number(a.cost_price) - Number(b.stock) * Number(b.cost_price);
          break;
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [ingredients, search, sortCol, sortDir]);

  // prep-linked ingredient IDs (managed by prep batch system, not manually)
  const [prepIngredientIds, setPrepIngredientIds] = useState<Set<string>>(new Set());

  // unit change
  const [unitChangeIng, setUnitChangeIng] = useState<IngredientRow | null>(null);
  const [unitChangeNew, setUnitChangeNew] = useState('');
  const [unitChangeRatio, setUnitChangeRatio] = useState('1');
  const [unitChangeRecipes, setUnitChangeRecipes] = useState<
    { id: string; name: string; currentQty: number }[]
  >([]);
  const [unitChangeChoices, setUnitChangeChoices] = useState<
    { id: string; name: string; currentQty: number }[]
  >([]);
  const [unitChangeSaving, setUnitChangeSaving] = useState(false);
  const [unitChangeConfirming, setUnitChangeConfirming] = useState(false);

  // delete flow: confirm first, then check FK blockers
  const [confirmDeleteIng, setConfirmDeleteIng] = useState<IngredientRow | null>(null);
  const [deleteChecking, setDeleteChecking] = useState(false);
  const [deleteBlocked, setDeleteBlocked] = useState<{
    ing: IngredientRow;
    reasons: string[];
  } | null>(null);

  // merge flow (regular ingredients only — not prep-linked)
  const [mergeOpen, setMergeOpen] = useState(false);
  const [mergeKeepId, setMergeKeepId] = useState('');
  const [mergeRemoveId, setMergeRemoveId] = useState('');
  const [mergePreviewLoading, setMergePreviewLoading] = useState(false);
  const [mergeSaving, setMergeSaving] = useState(false);
  const [mergePreview, setMergePreview] = useState<{
    recipeLines: { recipeName: string; quantity: number; willCombine: boolean }[];
    substituteCount: number;
    choiceOptionCount: number;
    purchaseOrderCount: number;
    transactionCount: number;
    combinedStock: number;
    combinedCost: number;
    unitMismatch: boolean;
  } | null>(null);

  // history modal
  const [historyFor, setHistoryFor] = useState<IngredientRow | null>(null);
  const [historyRows, setHistoryRows] = useState<InvTxRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyPage, setHistoryPage] = useState(0);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [usedBySuppliers, setUsedBySuppliers] = useState<string[]>([]);
  const [recipesFor, setRecipesFor] = useState<IngredientRow | null>(null);
  const [recipeLinks, setRecipeLinks] = useState<RecipeLink[]>([]);
  const [recipesLoading, setRecipesLoading] = useState(false);
  const HISTORY_PAGE_SIZE = 20;

  async function load() {
    if (!supabase) return;
    setLoading(true);
    setError(null);
    const [ingRes, prepRes] = await Promise.all([
      supabase.from('ingredients').select('*').order('name'),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (supabase as any)
        .from('recipes')
        .select('prep_ingredient_id')
        .eq('is_prep', true)
        .not('prep_ingredient_id', 'is', null),
    ]);
    if (ingRes.error) setError(ingRes.error.message);
    else setIngredients(ingRes.data);
    const prepIds = new Set(
      ((prepRes.data ?? []) as { prep_ingredient_id: string }[])
        .map((r) => r.prep_ingredient_id)
        .filter(Boolean),
    );
    setPrepIngredientIds(prepIds);
    setLoading(false);
  }

  useEffect(() => {
    void load();
  }, []);

  const mergeableIngredients = useMemo(
    () =>
      [...ingredients]
        .filter((i) => !prepIngredientIds.has(i.id))
        .sort((a, b) => a.name.localeCompare(b.name)),
    [ingredients, prepIngredientIds],
  );

  function openMergeModal(preselectRemoveId?: string) {
    setMergeOpen(true);
    setMergeKeepId('');
    setMergeRemoveId(preselectRemoveId ?? '');
    setMergePreview(null);
    setError(null);
  }

  function closeMergeModal() {
    setMergeOpen(false);
    setMergeKeepId('');
    setMergeRemoveId('');
    setMergePreview(null);
    setMergePreviewLoading(false);
    setMergeSaving(false);
  }

  async function loadMergePreview(keepId: string, removeId: string) {
    if (!supabase || !keepId || !removeId || keepId === removeId) {
      setMergePreview(null);
      return;
    }
    const keep = ingredients.find((i) => i.id === keepId);
    const remove = ingredients.find((i) => i.id === removeId);
    if (!keep || !remove) {
      setMergePreview(null);
      return;
    }
    if (prepIngredientIds.has(keepId) || prepIngredientIds.has(removeId)) {
      setMergePreview(null);
      return;
    }
    if (keep.unit !== remove.unit) {
      setMergePreview({
        recipeLines: [],
        substituteCount: 0,
        choiceOptionCount: 0,
        purchaseOrderCount: 0,
        transactionCount: 0,
        combinedStock: keep.stock + remove.stock,
        combinedCost: 0,
        unitMismatch: true,
      });
      return;
    }

    setMergePreviewLoading(true);
    const [
      mergeItemsRes,
      keepItemsRes,
      subsRes,
      choiceRes,
      poRes,
      txRes,
    ] = await Promise.all([
      supabase.from('recipe_items').select('recipe_id, quantity').eq('ingredient_id', removeId),
      supabase.from('recipe_items').select('recipe_id').eq('ingredient_id', keepId),
      supabase
        .from('recipe_item_substitutes')
        .select('id', { count: 'exact', head: true })
        .eq('ingredient_id', removeId),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (supabase as any)
        .from('recipe_choice_options')
        .select('id', { count: 'exact', head: true })
        .eq('ingredient_id', removeId),
      supabase
        .from('purchase_order_items')
        .select('id', { count: 'exact', head: true })
        .eq('ingredient_id', removeId),
      supabase
        .from('inventory_transactions')
        .select('id', { count: 'exact', head: true })
        .eq('ingredient_id', removeId),
    ]);

    const keepRecipeIds = new Set((keepItemsRes.data ?? []).map((r) => r.recipe_id as string));
    const mergeItems = mergeItemsRes.data ?? [];
    const recipeIds = [...new Set(mergeItems.map((r) => r.recipe_id as string))];
    let recipeNameMap = new Map<string, string>();
    if (recipeIds.length > 0) {
      const { data: recipeData } = await supabase
        .from('recipes')
        .select('id, name')
        .in('id', recipeIds);
      recipeNameMap = new Map((recipeData ?? []).map((r) => [r.id, r.name]));
    }

    const recipeLines = mergeItems.map((ri) => ({
      recipeName: recipeNameMap.get(ri.recipe_id as string) ?? '—',
      quantity: Number(ri.quantity),
      willCombine: keepRecipeIds.has(ri.recipe_id as string),
    }));

    const totalStock = Number(keep.stock) + Number(remove.stock);
    let combinedCost = 0;
    if (totalStock <= 0) {
      combinedCost = Number(keep.cost_price) || Number(remove.cost_price);
    } else if (Number(keep.cost_price) > 0 && Number(remove.cost_price) > 0) {
      combinedCost =
        (Number(keep.stock) * Number(keep.cost_price) +
          Number(remove.stock) * Number(remove.cost_price)) /
        totalStock;
    } else {
      combinedCost = Number(keep.cost_price) || Number(remove.cost_price);
    }

    setMergePreview({
      recipeLines,
      substituteCount: subsRes.count ?? 0,
      choiceOptionCount: choiceRes.count ?? 0,
      purchaseOrderCount: poRes.count ?? 0,
      transactionCount: txRes.count ?? 0,
      combinedStock: totalStock,
      combinedCost: Math.round(combinedCost * 100) / 100,
      unitMismatch: false,
    });
    setMergePreviewLoading(false);
  }

  useEffect(() => {
    if (!mergeOpen || !mergeKeepId || !mergeRemoveId) {
      setMergePreview(null);
      return;
    }
    void loadMergePreview(mergeKeepId, mergeRemoveId);
  }, [mergeOpen, mergeKeepId, mergeRemoveId, ingredients, prepIngredientIds]);

  async function executeMerge() {
    if (!supabase || !clientId || !mergeKeepId || !mergeRemoveId) return;
    if (prepIngredientIds.has(mergeKeepId) || prepIngredientIds.has(mergeRemoveId)) {
      setError(t('ingredients.mergePrepBlocked'));
      return;
    }
    setMergeSaving(true);
    setError(null);
    const { data, error: err } = await supabase.rpc('merge_ingredients', {
      p_client_id: clientId,
      p_keep_id: mergeKeepId,
      p_merge_id: mergeRemoveId,
    });
    setMergeSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    closeMergeModal();
    void load();
    if (data) {
      const d = data as { keep_name: string; merge_name: string; new_stock: number };
      setImportResult(
        `${t('ingredients.mergeSuccess')} "${d.merge_name}" → "${d.keep_name}" · stock ${d.new_stock}`,
      );
    }
  }

  async function addIngredient() {
    if (!supabase) return;
    if (!clientId) {
      setError(t('ingredients.noClient'));
      return;
    }
    if (!form.name.trim()) {
      setError(t('ingredients.nameRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    const { error: err } = await supabase.from('ingredients').insert({
      client_id: clientId,
      name: form.name.trim(),
      unit: form.unit,
      stock: Number(form.stock) || 0,
      low_stock_threshold: Number(form.lowStockThreshold) || 0,
      cost_price: Number(form.costPrice) || 0,
    });
    setSaving(false);
    if (err) {
      setError(err.message);
      return;
    }
    setForm(BLANK);
    void load();
  }

  /** Returns a display name for the current auth user (name → email → id). */
  async function getCurrentUserName(): Promise<string> {
    if (!supabase) return 'unknown';
    const { data } = await supabase.auth.getUser();
    const u = data?.user;
    if (!u) return 'unknown';
    return u.user_metadata?.full_name || u.email || u.id;
  }

  async function restock(id: string) {
    if (!supabase || restockQty === '') return;
    const newStock = Number(restockQty);
    if (isNaN(newStock)) {
      setError(t('ingredients.invalidNumber'));
      return;
    }
    setError(null);
    const ingredient = ingredients.find((i) => i.id === id)!;
    const delta = newStock - ingredient.stock;
    const by = await getCurrentUserName();

    const { error: updateErr } = await supabase
      .from('ingredients')
      .update({ stock: newStock })
      .eq('id', id);
    if (updateErr) {
      setError(updateErr.message);
      return;
    }
    await supabase.from('inventory_transactions').insert({
      client_id: ingredient.client_id,
      ingredient_id: id,
      delta,
      type: 'restock',
      note: `Stock set to ${newStock} ${ingredient.unit} (was ${ingredient.stock}, by ${by})`,
    });

    setRestockId(null);
    setRestockQty('');
    void load();
  }

  /** Manual stock adjustment — supports negative deltas for waste/correction. */
  async function adjust(id: string) {
    if (!supabase) return;
    const delta = Number(adjustDelta);
    if (!delta) {
      setError(t('ingredients.invalidDelta'));
      return;
    }
    setError(null);
    const ingredient = ingredients.find((i) => i.id === id)!;
    const newStock = Math.max(0, ingredient.stock + delta);
    const by = await getCurrentUserName();

    const { error: updateErr } = await supabase
      .from('ingredients')
      .update({ stock: newStock })
      .eq('id', id);
    if (updateErr) {
      setError(updateErr.message);
      return;
    }
    await supabase.from('inventory_transactions').insert({
      client_id: ingredient.client_id,
      ingredient_id: id,
      delta,
      type: 'adjustment',
      note: adjustNote.trim()
        ? `${adjustNote.trim()} (by ${by})`
        : `Manual adjustment: ${delta > 0 ? '+' : ''}${delta} ${ingredient.unit} (by ${by})`,
    });

    setAdjustId(null);
    setAdjustDelta('');
    setAdjustNote('');
    void load();
  }

  /** Update the low-stock threshold for an ingredient. */
  async function saveLowStock(id: string) {
    if (!supabase) return;
    const val = Number(lowStockEditVal);
    if (isNaN(val) || val < 0) {
      setError(t('ingredients.invalidNonNegative'));
      return;
    }
    setError(null);
    const { error: err } = await supabase
      .from('ingredients')
      .update({ low_stock_threshold: val })
      .eq('id', id);
    if (err) {
      setError(err.message);
      return;
    }
    setLowStockEditId(null);
    setLowStockEditVal('');
    void load();
  }

  /** Update the reorder quantity for an ingredient. */
  async function saveReorderQty(id: string) {
    if (!supabase) return;
    const val = Number(reorderEditVal);
    if (isNaN(val) || val < 0) {
      setError(t('ingredients.invalidNonNegative'));
      return;
    }
    setError(null);
    const { error: err } = await supabase
      .from('ingredients')
      .update({ reorder_quantity: val })
      .eq('id', id);
    if (err) {
      setError(err.message);
      return;
    }
    setReorderEditId(null);
    setReorderEditVal('');
    void load();
  }

  /** Update the cost price for an ingredient. */
  async function saveCost(id: string) {
    if (!supabase) return;
    const val = Number(costEditVal);
    if (isNaN(val) || val < 0) {
      setError(t('ingredients.invalidNonNegative'));
      return;
    }
    setError(null);
    const { error: err } = await supabase
      .from('ingredients')
      .update({ cost_price: val })
      .eq('id', id);
    if (err) {
      setError(err.message);
      return;
    }
    setCostEditId(null);
    setCostEditVal('');
    void load();
  }

  /** Rename an ingredient. */
  async function saveName(id: string) {
    if (!supabase) return;
    const name = nameEditVal.trim();
    if (!name) {
      setError(t('ingredients.nameRequired'));
      return;
    }
    setError(null);
    const { error: err } = await supabase.from('ingredients').update({ name }).eq('id', id);
    if (err) {
      setError(err.message);
      return;
    }
    setNameEditId(null);
    setNameEditVal('');
    void load();
  }

  async function openHistory(ing: IngredientRow) {
    setHistoryFor(ing);
    setHistoryPage(0);
    setHistoryRows([]);
    setHistoryTotal(0);
    setUsedBySuppliers([]);
    if (!supabase || !clientId) return;

    const supplierNames = new Set<string>();
    if (ing.preferred_supplier_id) {
      const { data: pref } = await supabase
        .from('suppliers')
        .select('name, display_name')
        .eq('id', ing.preferred_supplier_id)
        .maybeSingle();
      const label = pref?.display_name || pref?.name;
      if (label) supplierNames.add(label);
    }
    const { data: goods } = await supabase
      .from('invoice_goods')
      .select('invoice_id')
      .eq('ingredient_id', ing.id);
    const invoiceIds = [...new Set((goods ?? []).map((g) => g.invoice_id).filter(Boolean))];
    if (invoiceIds.length) {
      const { data: invoices } = await supabase
        .from('invoices')
        .select('supplier_name')
        .in('id', invoiceIds);
      for (const inv of invoices ?? []) {
        if (inv.supplier_name) supplierNames.add(inv.supplier_name);
      }
    }
    setUsedBySuppliers([...supplierNames].sort());
  }

  async function openRecipes(ing: IngredientRow) {
    persistRecipesModalId(ing.id);
    setRecipesFor(ing);
    setRecipeLinks([]);
    setRecipesLoading(true);
    const links = await loadRecipesUsingIngredient(ing.id);
    setRecipeLinks(links);
    setRecipesLoading(false);
  }

  function closeRecipes() {
    persistRecipesModalId(null);
    setRecipesFor(null);
    setRecipeLinks([]);
  }

  useEffect(() => {
    if (loading) return;
    const id = readRecipesModalId();
    if (!id) return;
    const ing = ingredients.find((row) => row.id === id);
    if (!ing) {
      persistRecipesModalId(null);
      return;
    }
    if (recipesFor?.id === ing.id) return;
    void openRecipes(ing);
    // Restore once after the ingredient list is ready (back from a recipe, or remount).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, ingredients]);

  useEffect(() => {
    if (!supabase || !historyFor) return;

    setHistoryLoading(true);
    const from = historyPage * HISTORY_PAGE_SIZE;
    const to = from + HISTORY_PAGE_SIZE - 1;

    void supabase
      .from('inventory_transactions')
      .select('*', { count: 'exact' })
      .eq('ingredient_id', historyFor.id)
      .order('created_at', { ascending: false })
      .range(from, to)
      .then(({ data, error: err, count }) => {
        setHistoryLoading(false);
        if (err) setError(err.message);
        else {
          setHistoryRows(data ?? []);
          setHistoryTotal(count ?? 0);
        }
      });
  }, [historyFor, historyPage]);

  async function openLinkedInvoice(tx: InvTxRow) {
    if (!supabase || !tx.invoice_id) return;
    const { data, error: err } = await supabase
      .from('invoices')
      .select('tax_invoice_id, serial_no, issued_at')
      .eq('id', tx.invoice_id)
      .maybeSingle();
    if (err || !data?.tax_invoice_id) {
      setError(err?.message ?? t('ingredients.invoiceMissing'));
      return;
    }
    const day = (data.issued_at ?? tx.created_at).slice(0, 10);
    const anchor = new Date(`${day}T12:00:00`);
    const shift = (delta: number) => {
      const next = new Date(anchor);
      next.setDate(next.getDate() + delta);
      const pad = (n: number) => String(n).padStart(2, '0');
      return `${next.getFullYear()}-${pad(next.getMonth() + 1)}-${pad(next.getDate())}`;
    };
    const params = new URLSearchParams({
      from: shift(-1),
      to: shift(1),
      open: data.tax_invoice_id,
    });
    if (data.serial_no) params.set('serial', data.serial_no);
    onNavigate?.('invoices');
    window.history.replaceState(null, '', `#/invoices?${params.toString()}`);
  }

  async function removeIngredient(id: string) {
    if (!supabase) return;
    const { error: err } = await supabase.from('ingredients').delete().eq('id', id);
    if (err) setError(err.message);
    else void load();
  }

  /** Pre-check all FK-blocking tables, collect human-readable names, show blocker modal or delete. */
  async function checkAndDelete(ing: IngredientRow) {
    if (!supabase) return;
    setError(null);
    setConfirmDeleteIng(null);
    setDeleteChecking(true);

    const reasons: string[] = [];

    // 1. recipe_items (direct ingredient usage)
    const { data: riData } = await supabase
      .from('recipe_items')
      .select('recipe_id')
      .eq('ingredient_id', ing.id);
    if (riData && riData.length > 0) {
      const recipeIds = [...new Set(riData.map((r) => r.recipe_id as string))];
      const { data: recipeData } = await supabase
        .from('recipes')
        .select('name')
        .in('id', recipeIds);
      (recipeData ?? []).forEach((r) => reasons.push(t('ingredients.blockRecipe', { name: r.name })));
    }

    // 2. recipe_item_substitutes (ingredient used as a swap option)
    const { data: subData } = await (supabase
      .from('recipe_item_substitutes')
      .select('recipe_item_id')
      .eq('ingredient_id', ing.id) as unknown as Promise<{
      data: { recipe_item_id: string }[] | null;
    }>);
    if (subData && subData.length > 0) {
      const riIds = [...new Set(subData.map((r) => r.recipe_item_id))];
      const { data: riData2 } = await supabase
        .from('recipe_items')
        .select('recipe_id')
        .in('id', riIds);
      if (riData2 && riData2.length > 0) {
        const recipeIds2 = [...new Set(riData2.map((r) => r.recipe_id as string))];
        const { data: recipeData2 } = await supabase
          .from('recipes')
          .select('name')
          .in('id', recipeIds2);
        (recipeData2 ?? []).forEach((r) =>
          reasons.push(t('ingredients.blockSubstitute', { name: r.name })),
        );
      }
    }

    // 3. purchase_order_items (referenced in a purchase order line)
    const { data: poData } = await supabase
      .from('purchase_order_items')
      .select('id')
      .eq('ingredient_id', ing.id);
    if (poData && poData.length > 0) {
      reasons.push(t('ingredients.blockPoItems', { count: poData.length }));
    }

    setDeleteChecking(false);
    if (reasons.length > 0) {
      setDeleteBlocked({ ing, reasons });
    } else {
      await removeIngredient(ing.id);
    }
  }

  async function openUnitChange(ing: IngredientRow) {
    setUnitChangeIng(ing);
    setUnitChangeNew(ing.unit);
    setUnitChangeRatio('1');
    setUnitChangeRecipes([]);
    setUnitChangeChoices([]);
    setUnitChangeConfirming(false);
    if (!supabase) return;
    const { data: riData } = await supabase
      .from('recipe_items')
      .select('id, recipe_id, quantity')
      .eq('ingredient_id', ing.id);
    if (riData && riData.length > 0) {
      const recipeIds = [...new Set(riData.map((r) => r.recipe_id as string))];
      const { data: recipeData } = await supabase
        .from('recipes')
        .select('id, name')
        .in('id', recipeIds);
      const nameById = Object.fromEntries((recipeData ?? []).map((r) => [r.id, r.name]));
      setUnitChangeRecipes(
        riData.map((ri) => ({
          id: ri.id,
          name: nameById[ri.recipe_id as string] ?? t('common.unknown'),
          currentQty: ri.quantity,
        })),
      );
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { data: choiceOpts } = await (supabase as any)
      .from('recipe_choice_options')
      .select('id, quantity, group_id')
      .eq('ingredient_id', ing.id);
    if (choiceOpts && choiceOpts.length > 0) {
      const groupIds = [...new Set(choiceOpts.map((o: { group_id: string }) => o.group_id))];
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { data: groups } = await (supabase as any)
        .from('recipe_choice_groups')
        .select('id, recipe_id')
        .in('id', groupIds);
      const recipeIds = [
        ...new Set((groups ?? []).map((g: { recipe_id: string }) => g.recipe_id as string)),
      ] as string[];
      const { data: recipeData } =
        recipeIds.length > 0
          ? await supabase.from('recipes').select('id, name').in('id', recipeIds)
          : { data: [] as { id: string; name: string }[] };
      const recipeNameById = Object.fromEntries((recipeData ?? []).map((r) => [r.id, r.name]));
      const recipeByGroup = Object.fromEntries(
        (groups ?? []).map((g: { id: string; recipe_id: string }) => [
          g.id,
          recipeNameById[g.recipe_id] ?? t('common.unknown'),
        ]),
      );
      setUnitChangeChoices(
        choiceOpts.map((o: { id: string; quantity: number; group_id: string }) => ({
          id: o.id,
          name: recipeByGroup[o.group_id] ?? t('common.unknown'),
          currentQty: Number(o.quantity),
        })),
      );
    }
  }

  async function saveUnitChange() {
    if (!supabase || !unitChangeIng || !unitChangeNew.trim()) return;
    const ratio = parseFloat(unitChangeRatio);
    if (!ratio || ratio <= 0) return;
    setUnitChangeSaving(true);
    setError(null);

    const round4 = (n: number) => Math.round(n * 10000) / 10000;
    const round2 = (n: number) => Math.round(n * 100) / 100;
    const newUnit = unitChangeNew.trim();
    const repairLedgerOnly = newUnit === unitChangeIng.unit && ratio !== 1;

    try {
      if (!repairLedgerOnly) {
        const newCostPrice =
          ratio !== 1 ? round2(Number(unitChangeIng.cost_price) * ratio) : Number(unitChangeIng.cost_price);

        const { error: ingErr } = await supabase
          .from('ingredients')
          .update({
            unit: newUnit,
            stock: round4(unitChangeIng.stock / ratio),
            low_stock_threshold: round4(unitChangeIng.low_stock_threshold / ratio),
            reorder_quantity: round4((unitChangeIng.reorder_quantity ?? 0) / ratio),
            cost_price: newCostPrice,
          })
          .eq('id', unitChangeIng.id);
        if (ingErr) throw new Error(ingErr.message);

        for (const ri of unitChangeRecipes) {
          const { error: riErr } = await supabase
            .from('recipe_items')
            .update({ quantity: round4(ri.currentQty / ratio) })
            .eq('id', ri.id);
          if (riErr) throw new Error(riErr.message);
        }

        const { data: subData } = await (supabase
          .from('recipe_item_substitutes')
          .select('id, quantity')
          .eq('ingredient_id', unitChangeIng.id) as unknown as Promise<{
          data: { id: string; quantity: number | null }[] | null;
        }>);
        for (const sub of subData ?? []) {
          if (sub.quantity != null) {
            const { error: subErr } = await supabase
              .from('recipe_item_substitutes')
              .update({ quantity: round4(sub.quantity / ratio) })
              .eq('id', sub.id);
            if (subErr) throw new Error(subErr.message);
          }
        }

        for (const co of unitChangeChoices) {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const { error: coErr } = await (supabase as any)
            .from('recipe_choice_options')
            .update({ quantity: round4(co.currentQty / ratio) })
            .eq('id', co.id);
          if (coErr) throw new Error(coErr.message);
        }
      }

      // Rescale transaction deltas to match the new unit (÷ ratio, cost × ratio).
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error: ledgerErr } = await (supabase as any).rpc(
        'rescale_ingredient_ledger_for_unit_change',
        {
          p_ingredient_id: unitChangeIng.id,
          p_ratio: ratio,
          p_new_unit: newUnit,
          p_selective_only: repairLedgerOnly,
        },
      );
      if (ledgerErr) throw new Error(ledgerErr.message);

      setUnitChangeSaving(false);
      setUnitChangeIng(null);
      void load();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t('ingredients.unitChangeFailed'));
      setUnitChangeSaving(false);
    }
  }

  function copyIngredient(ing: IngredientRow) {
    setForm({
      name: `${ing.name} (2)`,
      unit: ing.unit,
      stock: String(ing.stock),
      lowStockThreshold: String(ing.low_stock_threshold),
      costPrice: String(ing.cost_price),
    });
    // scroll to top so the user sees the pre-filled form
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function exportInventoryCsv() {
    const escape = (v: string | number) => {
      const s = String(v ?? '');
      return s.includes(',') || s.includes('"') || s.includes('\n')
        ? `"${s.replace(/"/g, '""')}"`
        : s;
    };
    const headers = [
      t('ingredients.csvName'),
      t('ingredients.csvUnit'),
      t('ingredients.csvStock'),
      t('ingredients.csvLowStock'),
      t('ingredients.csvReorder'),
      t('ingredients.csvCost'),
      t('ingredients.csvValue'),
      t('ingredients.csvActive'),
    ];
    const rows = ingredients.map((i) =>
      [
        i.name,
        i.unit,
        i.stock,
        i.low_stock_threshold,
        i.reorder_quantity,
        i.cost_price,
        Math.round(Number(i.stock) * Number(i.cost_price)),
        i.active === false ? t('ingredients.csvNo') : t('ingredients.csvYes'),
      ]
        .map(escape)
        .join(','),
    );
    const blob = new Blob(['\uFEFF' + [headers.join(','), ...rows].join('\n')], {
      type: 'text/csv;charset=utf-8;',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `inventory-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  }

  async function toggleActive(ing: IngredientRow) {
    if (!supabase) return;
    const next = ing.active === false;
    const { error: err } = await supabase.from('ingredients').update({ active: next }).eq('id', ing.id);
    if (err) setError(err.message);
    else void load();
  }

  async function handleCsvImport(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !supabase || !clientId) return;
    e.target.value = '';
    setImporting(true);
    setImportResult(null);
    setError(null);
    try {
      const text = await readFileAsText(file);
      const rows = parseCsv(text);
      // Detect header row: skip if first cell is non-numeric and looks like a label
      const dataRows = /^[a-z_]/i.test(rows[0]?.[0] ?? '') ? rows.slice(1) : rows;
      // Expected columns: name, unit, stock, low_stock_threshold, cost_price
      const inserts = dataRows
        .filter((r) => r[0]?.trim())
        .map((r) => ({
          client_id: clientId,
          name: (r[0] ?? '').trim(),
          unit: r[1]?.trim() || 'pcs',
          stock: Number(r[2]) || 0,
          low_stock_threshold: Number(r[3]) || 0,
          cost_price: Number(r[4]) || 0,
        }));
      if (inserts.length === 0) {
        setImportResult(t('ingredients.noValidRows'));
        return;
      }
      const { error: err } = await supabase.from('ingredients').insert(inserts);
      if (err) setError(err.message);
      else {
        setImportResult(t('ingredients.imported', { count: inserts.length }));
        void load();
      }
    } catch (err) {
      setError(String(err));
    } finally {
      setImporting(false);
    }
  }

  return (
    <section className="ui-stack">
      <PageHeader
        title={t('ingredients.title')}
        actions={
          <>
            <Button variant="secondary" size="sm" icon={<Download />} onClick={exportInventoryCsv}>
              {t('ingredients.exportCsv')}
            </Button>
            {role !== 'manager' && mergeableIngredients.length >= 2 && (
              <button className="btn-ghost btn-sm" onClick={() => openMergeModal()}>
                {t('ingredients.merge')}
              </button>
            )}
          </>
        }
      />

      {error && <Callout tone="danger">{error}</Callout>}

      {(() => {
        const lowStock = ingredients.filter(
          (i) => i.stock < 0 || (i.low_stock_threshold > 0 && i.stock <= i.low_stock_threshold),
        );
        if (lowStock.length === 0) return null;
        const names = lowStock.map((i) => i.name);
        const preview = names.slice(0, 6).join(', ');
        const overflow = names.length > 6 ? t('ingredients.moreCount', { count: names.length - 6 }) : '';
        return (
          <div
            className="warning-banner"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}
          >
            <span>
              <AlertTriangle className="ui-i" /> {t('ingredients.lowStockBanner', { count: lowStock.length })}{' '}
              <strong>{preview}{overflow}</strong>
            </span>
            {onNavigate && (
              <button className="btn btn-sm" onClick={() => onNavigate('stock-in')}>
                {t('ingredients.goToStockIn')}
              </button>
            )}
          </div>
        );
      })()}

      <div className="panel">
        <div className="add-form">
          <input
            placeholder={t('ingredients.namePlaceholder')}
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            onKeyDown={(e) => e.key === 'Enter' && void addIngredient()}
          />
          <select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
            {UNITS.map((u) => (
              <option key={u} value={u}>
                {u}
              </option>
            ))}
          </select>
          <input
            placeholder={t('ingredients.lowStockPlaceholder')}
            type="number"
            min="0"
            step="0.001"
            value={form.lowStockThreshold}
            onChange={(e) => setForm({ ...form, lowStockThreshold: e.target.value })}
            style={{ maxWidth: 120 }}
          />
          <input
            placeholder={t('ingredients.costPlaceholder')}
            type="number"
            min="0"
            step="1"
            value={form.costPrice}
            onChange={(e) => setForm({ ...form, costPrice: e.target.value })}
            style={{ maxWidth: 110 }}
          />
          {role !== 'manager' && (
            <input
              placeholder={t('ingredients.openingStockPlaceholder')}
              type="number"
              min="0"
              step="0.001"
              value={form.stock}
              onChange={(e) => setForm({ ...form, stock: e.target.value })}
              style={{ maxWidth: 120 }}
            />
          )}
          <button className="btn" onClick={addIngredient} disabled={saving}>
            {saving ? '…' : t('ingredients.addIngredient')}
          </button>
          <input
            type="search"
            className="panel-search"
            placeholder={t('common.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        {loading ? (
          <div className="empty-state">{t('common.loading')}</div>
        ) : (
          <table className="table">
            <thead>
              <tr>
                <th style={{ width: 44 }}>#</th>
                <th>
                  <button type="button" className="link sortable-th" onClick={() => toggleSort('name')}>
                    {t('ingredients.colName')}
                    {sortIndicator('name')}
                  </button>
                </th>
                <th>
                  <button type="button" className="link sortable-th" onClick={() => toggleSort('unit')}>
                    {t('ingredients.colUnit')}
                    {sortIndicator('unit')}
                  </button>
                </th>
                <th>
                  <button type="button" className="link sortable-th" onClick={() => toggleSort('stock')}>
                    {t('ingredients.colStock')}
                    {sortIndicator('stock')}
                  </button>
                </th>
                <th>
                  <button type="button" className="link sortable-th" onClick={() => toggleSort('low_stock')}>
                    {t('ingredients.lowStock')}
                    {sortIndicator('low_stock')}
                  </button>
                </th>
                <th>
                  <button type="button" className="link sortable-th" onClick={() => toggleSort('reorder')}>
                    {t('ingredients.colReorder')}
                    {sortIndicator('reorder')}
                  </button>
                </th>
                <th>
                  <button type="button" className="link sortable-th" onClick={() => toggleSort('cost_price')}>
                    {t('ingredients.colCost')}
                    {sortIndicator('cost_price')}
                  </button>
                </th>
                <th>
                  <button type="button" className="link sortable-th" onClick={() => toggleSort('value')}>
                    {t('ingredients.colValue')}
                    {sortIndicator('value')}
                  </button>
                </th>
                <th style={{ width: 140 }}></th>
              </tr>
            </thead>
            <tbody>
              {/* Admin and owner may override prep-batch management; managers may not. */}
              {(() => {
                const canEditPrep = role === 'admin' || role === 'owner';
                return displayedIngredients.map((ing, idx) => (
                  <tr
                    key={ing.id}
                    className={[
                      ing.stock <= ing.low_stock_threshold && ing.low_stock_threshold > 0
                        ? 'low-stock'
                        : '',
                      ing.active === false ? 'inactive' : '',
                    ]
                      .filter(Boolean)
                      .join(' ')}
                  >
                    <td className="text-subtle">{idx + 1}</td>
                    <td>
                      {nameEditId === ing.id ? (
                        <span className="inline-restock">
                          <input
                            type="text"
                            value={nameEditVal}
                            onChange={(e) => setNameEditVal(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') void saveName(ing.id);
                              if (e.key === 'Escape') {
                                setNameEditId(null);
                                setNameEditVal('');
                              }
                            }}
                            style={{ width: 180 }}
                            autoFocus
                          />
                          <button className="btn btn-sm" onClick={() => void saveName(ing.id)}>
                            <Check className="ui-i" />
                          </button>
                          <button
                            className="btn-ghost btn-sm"
                            onClick={() => {
                              setNameEditId(null);
                              setNameEditVal('');
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ) : (
                        <div className="ing-name-cell">
                          <button
                            className="link"
                            onClick={() => {
                              setNameEditId(ing.id);
                              setNameEditVal(ing.name);
                            }}
                          >
                            {ing.name}
                          </button>
                          {prepIngredientIds.has(ing.id) && (
                            <span
                              className="badge badge-success"
                              style={{ fontSize: 10 }}
                              title={t('ingredients.prepManaged')}
                            >
                              {t('ingredients.prep')}
                            </span>
                          )}
                          {ing.active === false && (
                            <span className="badge" style={{ fontSize: 10 }}>
                              {t('common.inactive')}
                            </span>
                          )}
                          <Button
                            variant="secondary"
                            size="sm"
                            icon={<ChefHat />}
                            onClick={() => void openRecipes(ing)}
                          >
                            {t('ingredients.recipes')}
                          </Button>
                        </div>
                      )}
                    </td>
                    <td>
                      <button
                        className="link"
                        title={t('ingredients.changeUnit')}
                        onClick={() => void openUnitChange(ing)}
                      >
                        {ing.unit}
                      </button>
                    </td>
                    <td>
                      {role === 'manager' || (prepIngredientIds.has(ing.id) && !canEditPrep) ? (
                        <span
                          style={{ color: 'var(--text-muted)' }}
                          title={
                            prepIngredientIds.has(ing.id) && !canEditPrep
                              ? t('ingredients.prepManagedHint')
                              : undefined
                          }
                        >
                          {Number(ing.stock).toLocaleString('en-US', { maximumFractionDigits: 3 })}{' '}
                          {ing.unit}
                        </span>
                      ) : restockId === ing.id ? (
                        <span className="inline-restock">
                          <input
                            type="number"
                            step="0.001"
                            placeholder={t('ingredients.newValuePlaceholder')}
                            value={restockQty}
                            onChange={(e) => setRestockQty(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') void restock(ing.id);
                              if (e.key === 'Escape') { setRestockId(null); setRestockQty(''); }
                            }}
                            style={{ width: 80 }}
                            autoFocus
                          />
                          <button className="btn btn-sm" onClick={() => void restock(ing.id)}>
                            {t('ingredients.set')}
                          </button>
                          <button
                            className="btn-ghost btn-sm"
                            onClick={() => {
                              setRestockId(null);
                              setRestockQty('');
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ) : (
                        <button
                          className="link"
                          onClick={() => {
                            setRestockId(ing.id);
                            setRestockQty(String(ing.stock));
                          }}
                        >
                          {Number(ing.stock).toLocaleString('en-US', { maximumFractionDigits: 3 })}{' '}
                          {ing.unit}
                        </button>
                      )}
                    </td>
                    <td style={{ color: 'var(--text-muted)' }}>
                      {lowStockEditId === ing.id ? (
                        <span className="inline-restock">
                          <input
                            type="number"
                            min="0"
                            step="0.001"
                            value={lowStockEditVal}
                            onChange={(e) => setLowStockEditVal(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') void saveLowStock(ing.id);
                              if (e.key === 'Escape') {
                                setLowStockEditId(null);
                                setLowStockEditVal('');
                              }
                            }}
                            style={{ width: 80 }}
                            autoFocus
                          />
                          <button className="btn btn-sm" onClick={() => void saveLowStock(ing.id)}>
                            <Check className="ui-i" />
                          </button>
                          <button
                            className="btn-ghost btn-sm"
                            onClick={() => {
                              setLowStockEditId(null);
                              setLowStockEditVal('');
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ) : (
                        <button
                          className="link"
                          onClick={() => {
                            setLowStockEditId(ing.id);
                            setLowStockEditVal(String(ing.low_stock_threshold));
                          }}
                        >
                          {ing.low_stock_threshold} {ing.unit}
                        </button>
                      )}
                    </td>
                    <td style={{ color: 'var(--text-muted)' }}>
                      {reorderEditId === ing.id ? (
                        <span className="inline-restock">
                          <input
                            type="number"
                            min="0"
                            step="0.001"
                            value={reorderEditVal}
                            onChange={(e) => setReorderEditVal(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') void saveReorderQty(ing.id);
                              if (e.key === 'Escape') {
                                setReorderEditId(null);
                                setReorderEditVal('');
                              }
                            }}
                            style={{ width: 80 }}
                            autoFocus
                          />
                          <button className="btn btn-sm" onClick={() => void saveReorderQty(ing.id)}>
                            <Check className="ui-i" />
                          </button>
                          <button
                            className="btn-ghost btn-sm"
                            onClick={() => {
                              setReorderEditId(null);
                              setReorderEditVal('');
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ) : (
                        <button
                          className="link"
                          onClick={() => {
                            setReorderEditId(ing.id);
                            setReorderEditVal(String(ing.reorder_quantity ?? 0));
                          }}
                        >
                          {(ing.reorder_quantity ?? 0) > 0
                            ? `${ing.reorder_quantity} ${ing.unit}`
                            : <span style={{ opacity: 0.45 }}>—</span>}
                        </button>
                      )}
                    </td>
                    <td>
                      {costEditId === ing.id ? (
                        <span className="inline-restock">
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={costEditVal}
                            onChange={(e) => setCostEditVal(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') void saveCost(ing.id);
                              if (e.key === 'Escape') {
                                setCostEditId(null);
                                setCostEditVal('');
                              }
                            }}
                            style={{ width: 90 }}
                            autoFocus
                          />
                          <button className="btn btn-sm" onClick={() => void saveCost(ing.id)}>
                            <Check className="ui-i" />
                          </button>
                          <button
                            className="btn-ghost btn-sm"
                            onClick={() => {
                              setCostEditId(null);
                              setCostEditVal('');
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ) : (
                        <button
                          className="link"
                          onClick={() => {
                            setCostEditId(ing.id);
                            setCostEditVal(String(ing.cost_price));
                          }}
                        >
                          {Number(ing.cost_price).toLocaleString('en-US', {
                            maximumFractionDigits: 2,
                          })}{' '}
                          ֏
                        </button>
                      )}
                    </td>
                    <td style={{ color: 'var(--text-muted)' }}>
                      {Math.round(ing.stock * ing.cost_price).toLocaleString('en-US')} ֏
                    </td>
                    <td>
                      {adjustId === ing.id ? (
                        <span className="inline-restock">
                          <input
                            type="number"
                            step="0.001"
                            placeholder={t('ingredients.adjustQtyPlaceholder')}
                            value={adjustDelta}
                            onChange={(e) => setAdjustDelta(e.target.value)}
                            style={{ width: 70 }}
                            autoFocus
                          />
                          <input
                            type="text"
                            placeholder={t('ingredients.notePlaceholder')}
                            value={adjustNote}
                            onChange={(e) => setAdjustNote(e.target.value)}
                            style={{ width: 100 }}
                          />
                          <button className="btn btn-sm" onClick={() => adjust(ing.id)}>
                            {t('ingredients.ok')}
                          </button>
                          <button
                            className="btn-ghost btn-sm"
                            onClick={() => {
                              setAdjustId(null);
                              setAdjustDelta('');
                              setAdjustNote('');
                            }}
                          >
                            ×
                          </button>
                        </span>
                      ) : (
                        <div className="row-actions">
                          {role !== 'manager' && (!prepIngredientIds.has(ing.id) || canEditPrep) && (
                            <button
                              className="btn-icon"
                              onClick={() => {
                                setAdjustId(ing.id);
                                setAdjustDelta('');
                                setAdjustNote('');
                              }}
                              title={t('ingredients.adjustTitle')}
                            >
                              ±
                            </button>
                          )}
                          <button
                            className="btn-icon"
                            onClick={() => void openHistory(ing)}
                            title={t('ingredients.viewHistory')}
                          >
                            <History className="ui-i" />
                          </button>
                          {role !== 'manager' && (
                            <button
                              className="btn-icon"
                              onClick={() => void toggleActive(ing)}
                              title={ing.active === false ? t('ingredients.markActive') : t('ingredients.markInactive')}
                            >
                              <CircleOff
                                className="ui-i"
                                style={{ opacity: ing.active === false ? 1 : 0.35 }}
                              />
                            </button>
                          )}
                          <button
                            className="btn-icon"
                            onClick={() => copyIngredient(ing)}
                            title={t('ingredients.copy')}
                          >
                            ⎘
                          </button>
                          {!prepIngredientIds.has(ing.id) && role !== 'manager' && (
                            <button
                              className="btn-icon"
                              onClick={() => openMergeModal(ing.id)}
                              title={t('ingredients.merge')}
                            >
                              ⤵
                            </button>
                          )}
                          <button
                            className="btn-icon danger"
                            onClick={() => setConfirmDeleteIng(ing)}
                            title={t('common.delete')}
                          >
                            <Trash2 className="ui-i" />
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                ));
              })()}
              {ingredients.length === 0 && (
                <tr>
                  <td
                    colSpan={8}
                    style={{
                      textAlign: 'center',
                      padding: '28px 20px',
                      color: 'var(--text-subtle)',
                    }}
                  >
                    {t('ingredients.noIngredients')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {role !== 'manager' && (
          <div className="csv-import">
            <input
              ref={csvRef}
              type="file"
              accept=".csv,text/csv"
              style={{ display: 'none' }}
              onChange={handleCsvImport}
            />
            <button
              className="btn-ghost btn-sm"
              onClick={() => csvRef.current?.click()}
              disabled={importing}
            >
              {importing ? '…' : `↑ ${t('ingredients.importCsv')}`}
            </button>
            <span className="csv-hint">
              {t('ingredients.csvHint')}
            </span>
            {importResult && <span className="csv-result">{importResult}</span>}
          </div>
        )}
      </div>

      {unitChangeIng && (() => {
        const ratio = parseFloat(unitChangeRatio);
        const validRatio = ratio > 0 && !isNaN(ratio);
        const round4 = (n: number) => +(n / (validRatio ? ratio : 1)).toFixed(4);
        const round2 = (n: number) => +(n * (validRatio ? ratio : 1)).toFixed(2);
        const isRename = validRatio && ratio === 1;
        const newUnit = unitChangeNew.trim() || unitChangeIng.unit;
        const isLedgerRepair = validRatio && ratio !== 1 && newUnit === unitChangeIng.unit;
        return (
          <div className="modal-overlay" onClick={() => !unitChangeSaving && setUnitChangeIng(null)}>
            <div
              className="modal"
              style={{ maxWidth: 560, width: '96vw' }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="modal-header">
                <h2>{t('ingredients.changeUnitTitle', { name: unitChangeIng.name })}</h2>
                <button className="btn-icon" disabled={unitChangeSaving} onClick={() => setUnitChangeIng(null)}>×</button>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginBottom: 20 }}>
                <label className="form-field">
                  <span className="form-label">{t('ingredients.newUnit')}</span>
                  <select
                    value={unitChangeNew}
                    onChange={(e) => { setUnitChangeNew(e.target.value); setUnitChangeConfirming(false); }}
                    disabled={unitChangeSaving}
                  >
                    {UNITS.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </label>

                <label className="form-field">
                  <span className="form-label">
                    {t('ingredients.conversionLabel', {
                      newUnit: newUnit || '…',
                      oldUnit: unitChangeIng.unit,
                    })}
                    <span className="form-hint">{t('ingredients.conversionHint')}</span>
                  </span>
                  <input
                    type="number"
                    min="0.0001"
                    step="any"
                    value={unitChangeRatio}
                    onChange={(e) => { setUnitChangeRatio(e.target.value); setUnitChangeConfirming(false); }}
                    disabled={unitChangeSaving}
                    style={{ maxWidth: 140 }}
                    autoFocus
                  />
                </label>
              </div>

              {validRatio && (
                <div style={{ marginBottom: 16 }}>
                  <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-subtle)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
                    {t('ingredients.previewChanges')}
                  </p>
                  <table className="table" style={{ fontSize: 13 }}>
                    <thead>
                      <tr>
                        <th>{t('ingredients.field')}</th>
                        <th>{t('ingredients.current')}</th>
                        <th>{t('ingredients.newCol')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>{t('ingredients.colUnit')}</td>
                        <td style={{ color: 'var(--text-muted)' }}>{unitChangeIng.unit}</td>
                        <td><strong>{newUnit}</strong></td>
                      </tr>
                      <tr>
                        <td>{t('ingredients.colStock')}</td>
                        <td style={{ color: 'var(--text-muted)' }}>{unitChangeIng.stock} {unitChangeIng.unit}</td>
                        <td>{round4(unitChangeIng.stock)} {newUnit}</td>
                      </tr>
                      <tr>
                        <td>{t('ingredients.costPriceField')}</td>
                        <td style={{ color: 'var(--text-muted)' }}>
                          {Number(unitChangeIng.cost_price).toLocaleString('en-US')} ֏ / {unitChangeIng.unit}
                        </td>
                        <td>
                          {isRename
                            ? `${Number(unitChangeIng.cost_price).toLocaleString('en-US')} ֏ / ${newUnit}`
                            : `${round2(Number(unitChangeIng.cost_price)).toLocaleString('en-US')} ֏ / ${newUnit}`}
                        </td>
                      </tr>
                      <tr>
                        <td>{t('ingredients.lowStock')}</td>
                        <td style={{ color: 'var(--text-muted)' }}>{unitChangeIng.low_stock_threshold} {unitChangeIng.unit}</td>
                        <td>{round4(unitChangeIng.low_stock_threshold)} {newUnit}</td>
                      </tr>
                      {(unitChangeIng.reorder_quantity ?? 0) > 0 && (
                        <tr>
                          <td>{t('ingredients.colReorder')}</td>
                          <td style={{ color: 'var(--text-muted)' }}>{unitChangeIng.reorder_quantity} {unitChangeIng.unit}</td>
                          <td>{round4(unitChangeIng.reorder_quantity ?? 0)} {newUnit}</td>
                        </tr>
                      )}
                      {unitChangeRecipes.map((ri) => (
                        <tr key={ri.id}>
                          <td>{t('ingredients.recipeLine', { name: ri.name })}</td>
                          <td style={{ color: 'var(--text-muted)' }}>{ri.currentQty} {unitChangeIng.unit}</td>
                          <td>{round4(ri.currentQty)} {newUnit}</td>
                        </tr>
                      ))}
                      {unitChangeChoices.map((co) => (
                        <tr key={co.id}>
                          <td>{t('ingredients.choiceLine', { name: co.name })}</td>
                          <td style={{ color: 'var(--text-muted)' }}>{co.currentQty} {unitChangeIng.unit}</td>
                          <td>{round4(co.currentQty)} {newUnit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <p style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 8 }}>
                    {isLedgerRepair
                      ? t('ingredients.ledgerRepairNote', { ratio, unit: newUnit })
                      : t('ingredients.convertNote')}
                  </p>
                </div>
              )}

              {!validRatio && unitChangeRatio !== '' && (
                <div className="error-banner" style={{ marginBottom: 12 }}>{t('ingredients.positiveRatio')}</div>
              )}

              {unitChangeConfirming ? (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                  <span style={{ fontSize: 13, color: 'var(--text)', flex: 1 }}>
                    {t('ingredients.confirmApply')}
                  </span>
                  <button
                    className="btn-ghost"
                    disabled={unitChangeSaving}
                    onClick={() => setUnitChangeConfirming(false)}
                  >
                    {t('common.back')}
                  </button>
                  <button
                    className="btn"
                    disabled={unitChangeSaving}
                    onClick={() => void saveUnitChange()}
                  >
                    {unitChangeSaving ? t('common.saving') : t('ingredients.yesApply')}
                  </button>
                </div>
              ) : (
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                  <button className="btn-ghost" onClick={() => setUnitChangeIng(null)}>
                    {t('common.cancel')}
                  </button>
                  <button
                    className="btn"
                    disabled={!validRatio || !newUnit}
                    onClick={() => setUnitChangeConfirming(true)}
                  >
                    {isRename
                      ? t('ingredients.renameTo', { unit: newUnit })
                      : isLedgerRepair
                        ? t('ingredients.repairLedgerBtn', { unit: newUnit })
                        : t('ingredients.convertTo', { unit: newUnit })}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {confirmDeleteIng && (
        <div className="modal-overlay" onClick={() => !deleteChecking && setConfirmDeleteIng(null)}>
          <div className="modal" style={{ maxWidth: 400 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('ingredients.deleteTitle')}</h2>
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 20 }}>
              {t('ingredients.deleteBody', { name: confirmDeleteIng.name })}
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button
                className="btn-ghost"
                disabled={deleteChecking}
                onClick={() => setConfirmDeleteIng(null)}
              >
                {t('common.cancel')}
              </button>
              <button
                className="btn"
                style={{ background: 'var(--danger)', borderColor: 'var(--danger)', color: '#fff' }}
                disabled={deleteChecking}
                onClick={() => void checkAndDelete(confirmDeleteIng)}
              >
                {deleteChecking ? t('ingredients.checking') : t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteBlocked && (
        <div className="modal-overlay" onClick={() => setDeleteBlocked(null)}>
          <div className="modal" style={{ maxWidth: 440 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('ingredients.cannotDeleteTitle', { name: deleteBlocked.ing.name })}</h2>
              <button className="btn-icon" onClick={() => setDeleteBlocked(null)}>×</button>
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 12 }}>
              {t('ingredients.cannotDeleteBody')}
            </p>
            <ul style={{ margin: 0, padding: '0 0 16px 20px', fontSize: 13, display: 'flex', flexDirection: 'column', gap: 4 }}>
              {deleteBlocked.reasons.map((r) => (
                <li key={r} style={{ color: 'var(--text)' }}>{r}</li>
              ))}
            </ul>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn-ghost" onClick={() => setDeleteBlocked(null)}>
                {t('common.close')}
              </button>
            </div>
          </div>
        </div>
      )}

      {mergeOpen && (
        <div className="modal-overlay" onClick={() => closeMergeModal()}>
          <div className="modal" style={{ maxWidth: 520 }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('ingredients.mergeTitle')}</h2>
              <button className="btn-icon" onClick={() => closeMergeModal()}>
                ×
              </button>
            </div>
            <p style={{ color: 'var(--text-muted)', fontSize: 13, marginBottom: 16 }}>
              {t('ingredients.mergeDescription')}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                <span style={{ fontWeight: 600 }}>{t('ingredients.mergeKeep')}</span>
                <select
                  value={mergeKeepId}
                  onChange={(e) => setMergeKeepId(e.target.value)}
                  className="field-select"
                >
                  <option value="">{t('ingredients.mergeSelectKeep')}</option>
                  {mergeableIngredients
                    .filter((i) => i.id !== mergeRemoveId)
                    .map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.name} ({i.unit}) · {t('ingredients.inStockSuffix', { stock: i.stock })}
                      </option>
                    ))}
                </select>
              </label>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 13 }}>
                <span style={{ fontWeight: 600 }}>{t('ingredients.mergeRemove')}</span>
                <select
                  value={mergeRemoveId}
                  onChange={(e) => setMergeRemoveId(e.target.value)}
                  className="field-select"
                >
                  <option value="">{t('ingredients.mergeSelectRemove')}</option>
                  {mergeableIngredients
                    .filter((i) => i.id !== mergeKeepId)
                    .map((i) => (
                      <option key={i.id} value={i.id}>
                        {i.name} ({i.unit}) · {t('ingredients.inStockSuffix', { stock: i.stock })}
                      </option>
                    ))}
                </select>
              </label>
            </div>

            {mergePreviewLoading && (
              <p className="muted" style={{ fontSize: 13 }}>
                {t('common.loading')}
              </p>
            )}

            {mergePreview && !mergePreviewLoading && (
              <div
                style={{
                  background: 'var(--surface-2)',
                  borderRadius: 6,
                  padding: '12px 14px',
                  fontSize: 13,
                  marginBottom: 16,
                }}
              >
                <div style={{ fontWeight: 600, marginBottom: 8 }}>{t('ingredients.mergePreview')}</div>
                {mergePreview.unitMismatch ? (
                  <p style={{ color: 'var(--danger)', margin: 0 }}>
                    {t('ingredients.mergeUnitMismatch')}
                  </p>
                ) : (
                  <>
                    <p style={{ margin: '0 0 8px' }}>
                      {t('ingredients.mergeCombinedStock')}:{' '}
                      <strong>
                        {mergePreview.combinedStock}{' '}
                        {ingredients.find((i) => i.id === mergeKeepId)?.unit}
                      </strong>
                      {' · '}
                      {t('ingredients.mergeCombinedCost')}:{' '}
                      <strong>{mergePreview.combinedCost.toLocaleString()} ֏</strong>
                    </p>
                    {mergePreview.recipeLines.length > 0 && (
                      <div style={{ marginBottom: 8 }}>
                        <div className="muted" style={{ marginBottom: 4 }}>
                          {t('ingredients.mergeRecipes')}:
                        </div>
                        <ul style={{ margin: 0, paddingLeft: 18 }}>
                          {mergePreview.recipeLines.map((line, i) => (
                            <li key={`${line.recipeName}-${i}`}>
                              {line.recipeName} — {line.quantity}{' '}
                              <span className="muted">
                                (
                                {line.willCombine
                                  ? t('ingredients.mergeWillCombine')
                                  : t('ingredients.mergeWillRepoint')}
                                )
                              </span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    <ul style={{ margin: 0, paddingLeft: 18 }} className="muted">
                      {mergePreview.substituteCount > 0 && (
                        <li>
                          {t('ingredients.mergeSubstitutes')}: {mergePreview.substituteCount}
                        </li>
                      )}
                      {mergePreview.choiceOptionCount > 0 && (
                        <li>
                          {t('ingredients.mergeChoiceOptions')}: {mergePreview.choiceOptionCount}
                        </li>
                      )}
                      {mergePreview.purchaseOrderCount > 0 && (
                        <li>
                          {t('ingredients.mergePurchaseLines')}: {mergePreview.purchaseOrderCount}
                        </li>
                      )}
                      {mergePreview.transactionCount > 0 && (
                        <li>
                          {t('ingredients.mergeTransactions')}: {mergePreview.transactionCount}
                        </li>
                      )}
                    </ul>
                  </>
                )}
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
              <button className="btn-ghost" disabled={mergeSaving} onClick={() => closeMergeModal()}>
                {t('common.cancel')}
              </button>
              <button
                className="btn"
                disabled={
                  mergeSaving ||
                  !mergeKeepId ||
                  !mergeRemoveId ||
                  mergePreviewLoading ||
                  !mergePreview ||
                  mergePreview.unitMismatch
                }
                onClick={() => void executeMerge()}
              >
                {mergeSaving ? '…' : t('ingredients.mergeConfirm')}
              </button>
            </div>
          </div>
        </div>
      )}

      {historyFor && (
        <div className="modal-overlay" onClick={() => setHistoryFor(null)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>{t('ingredients.historyTitle', { name: historyFor.name })}</h2>
              <button className="btn-icon" onClick={() => setHistoryFor(null)}>
                ×
              </button>
            </div>
            <div className="muted" style={{ padding: '0 20px 12px', fontSize: 13 }}>
              <div>
                <strong>{t('ingredients.suppliers')}</strong>{' '}
                {usedBySuppliers.length ? usedBySuppliers.join(', ') : t('ingredients.noSuppliers')}
              </div>
            </div>
            {historyLoading ? (
              <div className="empty-state">{t('common.loading')}</div>
            ) : historyRows.length === 0 ? (
              <div className="empty-state">{t('ingredients.noTx')}</div>
            ) : (
              <>
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('ingredients.colTime')}</th>
                      <th>{t('ingredients.colType')}</th>
                      <th>{t('ingredients.colDelta')}</th>
                      <th>{t('ingredients.colReference')}</th>
                      <th>{t('ingredients.colNote')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyRows.map((tx) => {
                      const linked = tx.delta > 0 && !!tx.invoice_id;
                      return (
                      <tr
                        key={tx.id}
                        className={linked ? 'tx-row-link' : undefined}
                        title={linked ? t('ingredients.openInvoice') : undefined}
                        onClick={() => {
                          if (linked) void openLinkedInvoice(tx);
                        }}
                      >
                        <td style={{ color: 'var(--text-muted)' }}>
                          {new Date(tx.created_at).toLocaleString('en-GB', {
                            dateStyle: 'short',
                            timeStyle: 'short',
                          })}
                        </td>
                        <td>{tx.type}</td>
                        <td
                          style={{
                            color: tx.delta < 0 ? 'var(--danger)' : 'var(--accent)',
                            fontWeight: 600,
                          }}
                        >
                          {tx.delta > 0 ? '+' : ''}
                          {tx.delta} {historyFor.unit}
                        </td>
                        <td
                          style={{
                            color: 'var(--text-subtle)',
                            fontFamily: 'monospace',
                            fontSize: 12,
                          }}
                        >
                          {linked ? t('ingredients.openInvoice') : (tx.reference_id ?? '—')}
                        </td>
                        <td style={{ color: 'var(--text-muted)' }}>{tx.note ?? '—'}</td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
                {historyTotal > HISTORY_PAGE_SIZE && (
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      paddingTop: 12,
                      justifyContent: 'flex-end',
                    }}
                  >
                    <button
                      className="btn-ghost btn-sm"
                      disabled={historyPage === 0 || historyLoading}
                      onClick={() => setHistoryPage((p) => p - 1)}
                    >
                      {t('common.prev')}
                    </button>
                    <span className="muted" style={{ fontSize: 13 }}>
                      {t('common.pageOf', {
                        page: historyPage + 1,
                        count: Math.ceil(historyTotal / HISTORY_PAGE_SIZE),
                      })}{' '}
                      · {t('ingredients.historyPageTotal', { total: historyTotal })}
                    </span>
                    <button
                      className="btn-ghost btn-sm"
                      disabled={
                        historyLoading ||
                        (historyPage + 1) * HISTORY_PAGE_SIZE >= historyTotal
                      }
                      onClick={() => setHistoryPage((p) => p + 1)}
                    >
                      {t('common.next')}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      )}

      <Modal
        open={recipesFor != null}
        onClose={closeRecipes}
        title={t('ingredients.recipesUsing', { name: recipesFor?.name ?? '' })}
        caption={t('ingredients.recipesUsingCaption')}
      >
        {recipesLoading ? (
          <div className="muted">{t('common.loading')}</div>
        ) : recipeLinks.length === 0 ? (
          <EmptyState compact title={t('ingredients.recipesNone')} />
        ) : (
          <div className="ing-recipe-list">
            {recipeLinks.map((r) => (
              <button
                key={r.id}
                type="button"
                className="ing-recipe-row"
                onClick={() => {
                  persistRecipesModalId(recipesFor?.id ?? null);
                  if (onNavigateToRecipe) onNavigateToRecipe(r.id);
                  else {
                    window.history.pushState(null, '', `#/recipes?recipe=${encodeURIComponent(r.id)}`);
                    onNavigate?.('recipes');
                  }
                }}
              >
                <span>{r.name}</span>
                <ChevronRight className="ui-i" />
              </button>
            ))}
          </div>
        )}
      </Modal>
    </section>
  );
}
